import { Component, computed, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { CancelIcon } from '../../../../../../../shared/ui/icons/cancel-icon/cancel-icon';
import { SubmitButton } from '../../../../../../../shared/ui/Buttons/submit-button/submit-button';
import { OrderOfferItem } from '../../order-offer-item/order-offer-item';
import { OrderBundleItem } from '../../order-bundle-item/order-bundle-item';
import { OrderProductItem } from '../../order-product-item/order-product-item';
import { setItemPackageAction } from '../../../../../../../store/order/confirm-package-order/confirm-package.actions';
import { ConvertShortageData } from '../../../actions/confirm-package.model';

@Component({
  selector: 'app-convert-to-products-popup',
  standalone: true,
  imports: [CommonModule, CancelIcon, SubmitButton, OrderOfferItem, OrderBundleItem, OrderProductItem],
  templateUrl: './convert-to-products-popup.html',
})
export class ConvertToProductsPopup {
  private locale = inject(LOCALE_ID);
  private store = inject(Store);

  isArabic = computed(() => this.locale.startsWith('ar'));

  data = input.required<ConvertShortageData>();

  closed = output<void>();
  confirmed = output<void>();

  packageState = input<Record<string, { packageQuantity: string | null; notPackageRows: any[] }>>({});
  isPrinted = input<boolean>(false);
  isPartiallyPackaged = input<boolean>(false);

  showWarning = signal(false);

  itemType = computed<'offer' | 'bundle'>(() => this.data()?.itemType ?? 'offer');

  orderItem = computed<any>(() => this.data()?.item);

  /** The offer/bundle source attached to the order item (`bxGyOffer`, `offer` or `bundle`). */
  offer = computed<any>(() => {
    const item = this.data()?.item;
    return item?.bxGyOffer ?? item?.offer ?? item?.bundle;
  });

  products = computed(() => this.data()?.products ?? []);

  /** Products that are available and will be converted into normal products. */
  availableProducts = computed(() =>
    this.products().filter(
      (p: any) => p.isAvailable === true || (+p.availableQuantity || 0) > 0 || (+p.packageQuantity || 0) > 0,
    ),
  );

  /** Resolves the product from a shortage row (`row.product` or the row itself). */
  rowProduct(row: any): any {
    return row?.product ?? row;
  }

  productTitle(row: any) {
    const product = this.rowProduct(row);
    return this.isArabic() ? product?.titleAr : product?.titleEn;
  }

  productPeoplePrice(row: any) {
    const product = this.rowProduct(row);
    return product?.peoplePrice ?? product?.customerPrice;
  }

  productStorePrice(row: any) {
    const product = this.rowProduct(row);
    return product?.storePrice ?? product?.businessPrice;
  }

  productTotalPrice(row: any) {
    const unit = +(this.productStorePrice(row) ?? 0);
    const qty = +row?.availableQuantity || 0;
    return +(unit * qty).toFixed(2);
  }

  productTitleForSource() {
    const source = this.offer();
    return this.isArabic() ? source?.titleAr : source?.titleEn;
  }

  onCancelClick() {
    this.closed.emit();
  }

  onConfirmClick() {
    if (!this.showWarning()) {
      this.showWarning.set(true);
      return;
    }

    const shortage = this.data();
    const products = this.availableProducts().map((row: any) => ({
      customerProductId: row.product?.id,
      quantity: row.availableQuantity,
    }));

    this.store.dispatch(
      setItemPackageAction({
        itemId: shortage.itemId,
        itemType: this.itemType(),
        action: 'CONVERT',
        products,
      }),
    );

    this.confirmed.emit();
    this.closed.emit();
  }
}